import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DatabaseBackup,
  Download,
  RefreshCw,
  Trash2,
  UploadCloud,
  ShieldAlert,
  Loader2,
  Archive,
} from "lucide-react";

const IST_DATETIME = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "—";
  }
};
const fmtSize = (bytes = 0) => {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function Backup() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirm, setConfirm] = useState(null); // {kind:"restore"|"delete"|"upload", ...}
  const uploadInputRef = useRef(null);

  useEffect(() => {
    if (user && !user.is_super_admin) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup");
      setBackups(data.backups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load backups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/backup");
      toast.success(`Backup created: ${data.id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally {
      setCreating(false);
    }
  };

  const downloadFile = async (b) => {
    try {
      const res = await api.get(`/backup/${b.id}/download`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = b.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const runRestore = async () => {
    if (!confirm) return;
    setRestoring(true);
    try {
      if (confirm.kind === "restore") {
        await api.post(`/backup/${confirm.backup.id}/restore`, {
          preserve_super_admin: true,
        });
        toast.success("Restore complete. Refreshing…");
        setConfirm(null);
        setTimeout(() => window.location.reload(), 900);
      } else if (confirm.kind === "upload") {
        const fd = new FormData();
        fd.append("file", confirm.file);
        fd.append("preserve_super_admin", "true");
        await api.post("/backup/restore-upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Restore from upload complete. Refreshing…");
        setConfirm(null);
        setTimeout(() => window.location.reload(), 900);
      } else if (confirm.kind === "delete") {
        await api.delete(`/backup/${confirm.backup.id}`);
        toast.success("Backup deleted");
        setConfirm(null);
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setRestoring(false);
    }
  };

  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.name.endsWith(".tar.gz")) {
      toast.error("Please choose a .tar.gz file.");
      return;
    }
    if (f.size > 512 * 1024 * 1024) {
      toast.error("File too large (max 512 MB).");
      return;
    }
    setConfirm({ kind: "upload", file: f });
  };

  return (
    <div className="p-6 space-y-6" data-testid="backup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <DatabaseBackup className="w-6 h-6 text-[#0055FF]" />
            Backup &amp; Restore
          </h1>
          <p className="text-slate-500 mt-1 text-sm max-w-2xl">
            A backup is a single <code className="mono text-xs">.tar.gz</code>{" "}
            archive of every collection in the database plus every uploaded
            file (attendance, team chat, WhatsApp media). Restore wipes current
            data and imports from an archive — your super-admin account is
            preserved so you don&apos;t get locked out.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => uploadInputRef.current?.click()}
            disabled={restoring}
            data-testid="backup-restore-upload"
          >
            <UploadCloud className="w-4 h-4 mr-1" />
            Restore from file
          </Button>
          <input
            ref={uploadInputRef}
            type="file"
            accept=".tar.gz,application/gzip"
            className="hidden"
            onChange={onFilePicked}
          />
          <Button
            variant="outline"
            onClick={load}
            disabled={loading || creating}
          >
            <RefreshCw className={"w-4 h-4 mr-1 " + (loading ? "animate-spin" : "")} />
            Refresh
          </Button>
          <Button
            onClick={create}
            disabled={creating || restoring}
            className="bg-[#0055FF] hover:bg-[#0040CC]"
            data-testid="backup-create"
          >
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <DatabaseBackup className="w-4 h-4 mr-1" />
                Create backup now
              </>
            )}
          </Button>
        </div>
      </div>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        <CardHeader className="border-b bg-slate-50/50">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Archive className="w-4 h-4 text-slate-500" />
            Saved backups ({backups.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="backup-table">
            <thead className="bg-slate-50 mono text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Backup ID</th>
                <th className="text-left px-4 py-3">Created (IST)</th>
                <th className="text-left px-4 py-3">Size</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-slate-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && backups.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center py-10 text-slate-500">
                    No backups yet. Click <b>Create backup now</b> to make your first one.
                  </td>
                </tr>
              )}
              {!loading &&
                backups.map((b) => (
                  <tr key={b.id} data-testid={`backup-row-${b.id}`}>
                    <td className="px-4 py-3 mono text-xs">{b.id}</td>
                    <td className="px-4 py-3 text-slate-700">
                      {IST_DATETIME(b.created_at)}
                    </td>
                    <td className="px-4 py-3 mono text-xs text-slate-700">
                      {fmtSize(b.size)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadFile(b)}
                          data-testid={`backup-download-${b.id}`}
                        >
                          <Download className="w-3.5 h-3.5 mr-1" />
                          Download
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-amber-700 border-amber-200 hover:bg-amber-50"
                          onClick={() =>
                            setConfirm({ kind: "restore", backup: b })
                          }
                          data-testid={`backup-restore-${b.id}`}
                        >
                          <UploadCloud className="w-3.5 h-3.5 mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() =>
                            setConfirm({ kind: "delete", backup: b })
                          }
                          data-testid={`backup-delete-${b.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Confirm dialog */}
      <Dialog
        open={!!confirm}
        onOpenChange={(o) => !o && !restoring && setConfirm(null)}
      >
        <DialogContent
          className="sm:max-w-md"
          data-testid="backup-confirm-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {confirm?.kind === "delete" ? (
                <>Delete backup?</>
              ) : (
                <>
                  <ShieldAlert className="w-5 h-5 text-amber-600" />
                  Restore from backup?
                </>
              )}
            </DialogTitle>
            <DialogDescription asChild>
              {confirm?.kind === "delete" ? (
                <span>
                  This permanently removes the archive{" "}
                  <b>{confirm?.backup?.id}</b> from the server. It doesn&apos;t
                  affect the running database.
                </span>
              ) : confirm?.kind === "upload" ? (
                <span>
                  Restore from <b>{confirm?.file?.name}</b>? All current data
                  (employees, customers, tasks, attendance, chat, WhatsApp
                  messages, uploads) will be replaced by the contents of the
                  archive. Your super-admin account will be preserved so you
                  can log back in.
                </span>
              ) : (
                <span>
                  Restore from <b>{confirm?.backup?.id}</b>? All current data
                  (employees, customers, tasks, attendance, chat, WhatsApp
                  messages, uploads) will be replaced. Your super-admin
                  account will be preserved.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirm(null)}
              disabled={restoring}
            >
              Cancel
            </Button>
            <Button
              onClick={runRestore}
              disabled={restoring}
              className={
                confirm?.kind === "delete"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-amber-600 hover:bg-amber-700 text-white"
              }
              data-testid="backup-confirm-submit"
            >
              {restoring ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Working…
                </>
              ) : confirm?.kind === "delete" ? (
                "Delete forever"
              ) : (
                "Restore now"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
